import { useMemo, useState, useRef, useEffect } from "react";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import { streamEndpoint, exportDraftDocx, updateDraftDocument } from "@/lib/api";
import { useDraftTypes, fetchDocument } from "@/hooks/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NotebookPen, Copy, Check, Download, Pencil, Eye, Save, FileText, Plus, Loader2 } from "lucide-react";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";

const FALLBACK_DRAFT_TYPES = [
  { key: "complaint", label_ar: "لائحة دعوى", label_en: "Statement of claim" },
  { key: "motion", label_ar: "لائحة جوابية / مذكرة دفاع", label_en: "Defense memorandum" },
  { key: "appeal_memo", label_ar: "مذكرة استئناف", label_en: "Appeal memorandum" },
  { key: "demand_letter", label_ar: "خطاب إنذار عدلي", label_en: "Judicial notice" },
  { key: "enforcement_petition", label_ar: "محضر طلب تنفيذ", label_en: "Enforcement request" },
  { key: "custom", label_ar: "وثيقة مخصصة", label_en: "Custom document" },
];

function draftTypeLabel(type, lang) {
  if (!type) return "";
  return lang === "ar" ? type.label_ar : type.label_en;
}

function formatRelativeTime(iso, lang) {
  if (!iso) return "";
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return lang === "ar" ? "الآن" : "now";
  if (diffMins < 60) return lang === "ar" ? `${diffMins} د` : `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return lang === "ar" ? `${diffHours} س` : `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return lang === "ar" ? `${diffDays} ي` : `${diffDays}d`;
}

function draftLabel(doc, lang) {
  const line = (doc.preview || doc.filename || "").trim().split("\n")[0];
  const title = line.length > 56 ? `${line.slice(0, 53)}…` : line;
  return title || (lang === "ar" ? "مسودة" : "Draft");
}

export default function DraftPane({
  model,
  drafts = [],
  caseId,
  headerTitle,
  suggestedForm,
  previousDraftsFirst = true,
  defaultJurisdiction = "",
  onDraftSaved,
  onDraftUpdated,
  onConversationCreated,
}) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const countryCode = user?.operating_country || "JO";
  const { data: draftTypesData } = useDraftTypes();
  const draftTypes = draftTypesData?.types?.length ? draftTypesData.types : FALLBACK_DRAFT_TYPES;
  const [instructions, setInstructions] = useState("");
  const [parties, setParties] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [output, setOutput] = useState("");
  const [savedDocumentId, setSavedDocumentId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [viewMode, setViewMode] = useState("preview");
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingDraftId, setLoadingDraftId] = useState(null);
  const [loadedDraftTitle, setLoadedDraftTitle] = useState("");
  const [docType, setDocType] = useState("complaint");
  const abortRef = useRef(null);
  const savedSnapshotRef = useRef("");

  useEffect(() => {
    if (!suggestedForm) return;
    if (suggestedForm.doc_type != null) setDocType(suggestedForm.doc_type);
    if (suggestedForm.instructions != null) setInstructions(suggestedForm.instructions);
    if (suggestedForm.parties != null) setParties(suggestedForm.parties);
    if (suggestedForm.jurisdiction != null) setJurisdiction(suggestedForm.jurisdiction);
  }, [suggestedForm]);

  useEffect(() => {
    if (jurisdiction.trim() || !defaultJurisdiction) return;
    setJurisdiction(defaultJurisdiction);
  }, [defaultJurisdiction, jurisdiction]);

  useEffect(() => {
    if (draftTypesData?.default) {
      setDocType((prev) => (draftTypes.some((item) => item.key === prev) ? prev : draftTypesData.default));
    }
  }, [draftTypesData?.default, draftTypes]);

  useEffect(() => {
    if (!draftTypes.some((type) => type.key === docType)) {
      setDocType(draftTypes[0]?.key || "complaint");
    }
  }, [draftTypes, docType]);

  const curatedInstructionsForType = (typeKey) => {
    const type = draftTypes.find((item) => item.key === typeKey);
    if (!type) return "";
    return (lang === "ar" ? type.instructions_ar : type.instructions_en) || "";
  };

  useEffect(() => {
    if (!draftTypesData?.default || instructions.trim()) return;
    const type = draftTypes.find((item) => item.key === draftTypesData.default);
    const curated = type
      ? (lang === "ar" ? type.instructions_ar : type.instructions_en) || ""
      : "";
    if (curated) setInstructions(curated);
  }, [draftTypesData?.default, draftTypes, lang, instructions]);

  const selectDraftType = (typeKey) => {
    if (streaming || typeKey === docType) return;
    const curated = curatedInstructionsForType(typeKey);
    const apply = () => {
      setDocType(typeKey);
      if (curated) setInstructions(curated);
      else if (typeKey === "custom") setInstructions("");
    };
    if (instructions.trim() && curated && instructions.trim() !== curated.trim()) {
      if (!confirmDiscardEdits()) return;
      setIsDirty(false);
    }
    apply();
  };

  const sortedDrafts = useMemo(
    () =>
      [...drafts].sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
      ),
    [drafts],
  );

  const exportTitle = loadedDraftTitle || `${draftTypeLabel(draftTypes.find((t) => t.key === docType), lang) || t(`types.${docType}`)}-${Date.now()}`;

  const resetDraftEditor = () => {
    setOutput("");
    setSavedDocumentId(null);
    setConversationId(null);
    setViewMode("preview");
    setIsDirty(false);
    setLoadedDraftTitle("");
    savedSnapshotRef.current = "";
  };

  const confirmDiscardEdits = () => {
    if (!isDirty) return true;
    return window.confirm(t("draft_discard_confirm"));
  };

  const startNewDraft = () => {
    if (!confirmDiscardEdits()) return;
    resetDraftEditor();
  };

  const loadDraft = async (draftMeta) => {
    if (streaming) return;
    if (savedDocumentId === draftMeta.id && output) return;
    if (!confirmDiscardEdits()) return;

    setLoadingDraftId(draftMeta.id);
    try {
      const full = await fetchDocument(draftMeta.id, { includeText: true });
      const text = full.text || "";
      setSavedDocumentId(full.id);
      setOutput(text);
      savedSnapshotRef.current = text;
      setIsDirty(false);
      setViewMode("preview");
      setConversationId(null);
      setLoadedDraftTitle(draftLabel(full, lang));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || t("draft_load_failed"));
    } finally {
      setLoadingDraftId(null);
    }
  };

  const handleOutputChange = (value) => {
    setOutput(value);
    setIsDirty(value !== savedSnapshotRef.current);
  };

  const generate = async () => {
    if (streaming) return;
    if (!instructions.trim()) {
      toast.error(lang === "ar" ? "اكتب التعليمات أولاً" : "Please enter instructions");
      return;
    }
    if (!confirmDiscardEdits()) return;
    setStreaming(true);
    resetDraftEditor();
    let acc = "";
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamEndpoint(
        "/draft",
        {
          doc_type: docType,
          instructions,
          parties,
          jurisdiction,
          language: lang,
          model,
          ...(caseId ? { case_id: caseId } : {}),
        },
        (evt) => {
          if (evt.conversation_id) {
            setConversationId(evt.conversation_id);
            onConversationCreated?.(evt.conversation_id);
          }
          if (evt.document) {
            setSavedDocumentId(evt.document.id);
            setLoadedDraftTitle(draftLabel(evt.document, lang));
            onDraftSaved?.(evt.document);
          }
          if (evt.delta) {
            acc += evt.delta;
            setOutput(acc);
          }
          if (evt.error) toast.error(evt.error);
        },
        controller.signal,
      );
      savedSnapshotRef.current = acc;
      setIsDirty(false);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setStreaming(false);
    }
  };

  const saveEdits = async () => {
    if (!output.trim()) {
      toast.error(lang === "ar" ? "لا يوجد محتوى للحفظ" : "Nothing to save");
      return;
    }
    if (!savedDocumentId) {
      toast.error(lang === "ar" ? "احفظ المسودة أولاً بالإنشاء" : "Generate a draft first");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateDraftDocument(savedDocumentId, output, conversationId);
      savedSnapshotRef.current = output;
      setIsDirty(false);
      onDraftUpdated?.(updated);
      toast.success(t("draft_updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const copyOutput = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadAsText = () => {
    if (!output.trim()) return;
    const blob = new Blob([output], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportTitle}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAsDocx = async () => {
    if (!output.trim()) return;
    setExporting(true);
    try {
      const blob = await exportDraftDocx(output, exportTitle, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportTitle}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("draft_exported"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setExporting(false);
    }
  };

  const placeholderKey = [
    "complaint",
    "motion",
    "appeal_memo",
    "demand_letter",
    "enforcement_petition",
    "contract",
    "memo",
    "letter",
    "official_letter",
    "settlement_agreement",
  ].includes(docType)
    ? docType
    : "memo";
  const canEdit = Boolean(output) && !streaming;
  const showEditor = canEdit && viewMode === "edit";

  const previousDraftsSection = (
    <div
      className={
        previousDraftsFirst ? "grid-border-b pb-5 mb-1" : "grid-border-t pt-5 mt-2"
      }
    >
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">{t("draft_previous")}</div>
        {(output || savedDocumentId) && (
          <button
            type="button"
            data-testid="new-draft-btn"
            onClick={startNewDraft}
            disabled={streaming}
            className="flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900 uppercase tracking-wider disabled:opacity-50"
          >
            <Plus size={11} />
            {t("draft_new")}
          </button>
        )}
      </div>

      {sortedDrafts.length === 0 ? (
        <p className="text-xs text-slate-400 leading-relaxed">{t("draft_no_previous")}</p>
      ) : (
        <div className="space-y-0 grid-border max-h-56 overflow-y-auto bg-white">
          {sortedDrafts.map((draft, idx) => {
            const active = savedDocumentId === draft.id;
            const loading = loadingDraftId === draft.id;
            return (
              <button
                key={draft.id}
                type="button"
                data-testid={`draft-item-${draft.id}`}
                onClick={() => loadDraft(draft)}
                disabled={streaming || loading}
                className={[
                  "w-full text-start px-3 py-2.5 transition-colors flex items-start gap-2",
                  idx > 0 ? "grid-border-t" : "",
                  active ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-800",
                  streaming || loading ? "opacity-60" : "",
                ].join(" ")}
              >
                {loading ? (
                  <Loader2 size={14} className="shrink-0 mt-0.5 animate-spin" />
                ) : (
                  <FileText size={14} className="shrink-0 mt-0.5 opacity-70" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">{draftLabel(draft, lang)}</span>
                  <span
                    className={[
                      "block text-[10px] mt-0.5",
                      active ? "text-white/60" : "text-slate-400",
                    ].join(" ")}
                  >
                    {formatRelativeTime(draft.created_at, lang)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const draftFormSection = (
    <>
      <div>
        <div className="eyebrow mb-2">{t("doc_type")}</div>
        <div className="grid grid-cols-2 gap-0 grid-border">
          {draftTypes.map((type, idx) => (
            <button
              key={type.key}
              data-testid={`draft-type-${type.key}`}
              onClick={() => selectDraftType(type.key)}
              disabled={streaming}
              className={[
                "px-3 py-2.5 text-xs transition-colors duration-150",
                idx % 2 === 1 ? "grid-border-s" : "",
                Math.floor(idx / 2) > 0 ? "grid-border-t" : "",
                docType === type.key ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50",
                streaming ? "opacity-60 cursor-not-allowed" : "",
              ].join(" ")}
            >
              {draftTypeLabel(type, lang) || t(`types.${type.key}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="eyebrow mb-2 block">{t("instructions")}</label>
        <Textarea
          data-testid="draft-instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={t(`placeholders.${placeholderKey}`)}
          rows={6}
          disabled={streaming}
          className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-slate-900 text-sm bg-white"
        />
      </div>

      <div>
        <label className="eyebrow mb-2 block">{t("parties")}</label>
        <Input
          data-testid="draft-parties"
          value={parties}
          onChange={(e) => setParties(e.target.value)}
          placeholder={lang === "ar" ? "أحمد محمد · شركة الأمل" : "John Doe · Company LLC"}
          disabled={streaming}
          className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-slate-900 bg-white"
        />
      </div>

      <div>
        <label className="eyebrow mb-2 block">{t("jurisdiction")}</label>
        <Input
          data-testid="draft-jurisdiction"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          placeholder={t(`placeholders.jurisdiction.${countryCode}`) || defaultJurisdiction}
          disabled={streaming}
          className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-slate-900 bg-white"
        />
      </div>

      <Button
        data-testid="generate-draft-btn"
        onClick={generate}
        disabled={streaming || !instructions.trim()}
        className="rounded-none w-full bg-slate-900 hover:bg-slate-800 text-white h-11 text-sm gap-2"
      >
        <NotebookPen size={14} />
        {streaming ? t("streaming") : t("generate")}
      </Button>
    </>
  );

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-[400px_1fr] bg-white" data-testid="draft-pane">
      <div className="grid-border-e p-6 overflow-y-auto bg-slate-50/40">
        <div className="flex items-center gap-2 mb-6">
          <NotebookPen size={18} className="text-slate-900" strokeWidth={1.6} />
          <h2 className="font-serif-display text-xl text-slate-900">
            {headerTitle || t("draft_title")}
          </h2>
        </div>

        <div className="space-y-5">
          {previousDraftsFirst ? (
            <>
              {previousDraftsSection}
              {draftFormSection}
            </>
          ) : (
            <>
              {draftFormSection}
              {previousDraftsSection}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col overflow-hidden">
        <div className="grid-border-b px-5 py-3 bg-slate-50/50 flex flex-wrap items-center gap-2">
          <span className="eyebrow">{t("nav_draft")} · {model}</span>

          {canEdit && (
            <div className="flex items-center grid-border overflow-hidden">
              <button
                type="button"
                data-testid="draft-preview-mode"
                onClick={() => setViewMode("preview")}
                className={[
                  "flex items-center gap-1.5 px-3 py-1.5 text-[11px] transition-colors",
                  viewMode === "preview" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
              >
                <Eye size={12} />
                {t("draft_preview")}
              </button>
              <button
                type="button"
                data-testid="draft-edit-mode"
                onClick={() => setViewMode("edit")}
                className={[
                  "flex items-center gap-1.5 px-3 py-1.5 text-[11px] grid-border-s transition-colors",
                  viewMode === "edit" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
              >
                <Pencil size={12} />
                {t("draft_edit")}
              </button>
            </div>
          )}

          {output && (
            <div className="ms-auto flex flex-wrap items-center gap-2">
              {isDirty && (
                <span className="text-[10px] text-amber-700 uppercase tracking-wider">
                  {t("draft_unsaved")}
                </span>
              )}
              {canEdit && savedDocumentId && (
                <Button
                  data-testid="save-draft-btn"
                  variant="outline"
                  size="sm"
                  onClick={saveEdits}
                  disabled={saving || !isDirty}
                  className="rounded-none h-8 text-[11px] gap-1.5 border-slate-300"
                >
                  <Save size={12} />
                  {saving ? t("draft_saving") : t("draft_save")}
                </Button>
              )}
              <button
                data-testid="copy-draft-btn"
                onClick={copyOutput}
                className="flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-slate-900 px-2 py-1"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? t("copied") : t("copy")}
              </button>
              <Button
                data-testid="download-draft-btn"
                variant="outline"
                size="sm"
                onClick={downloadAsText}
                disabled={!output.trim()}
                className="rounded-none h-8 text-[11px] gap-1.5 border-slate-300"
              >
                <Download size={12} />
                {t("draft_export_txt")}
              </Button>
              <Button
                data-testid="download-draft-docx-btn"
                size="sm"
                onClick={downloadAsDocx}
                disabled={!output.trim() || exporting}
                className="rounded-none h-8 text-[11px] gap-1.5 bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Download size={12} />
                {exporting ? t("draft_exporting") : t("draft_export_docx")}
              </Button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 paper-grain">
          {!output && !streaming && (
            <div className="h-full flex flex-col items-center justify-center text-center text-sm text-slate-400 px-6">
              <NotebookPen size={28} strokeWidth={1.3} className="mb-3 opacity-50" />
              <p>{t("draft_empty_state")}</p>
              {sortedDrafts.length > 0 && (
                <p className="text-xs mt-2 text-slate-400">{t("draft_empty_hint")}</p>
              )}
            </div>
          )}
          {(output || streaming) && (
            <div className="max-w-3xl mx-auto bg-white grid-border min-h-[min(100%,32rem)]">
              {showEditor ? (
                <Textarea
                  data-testid="draft-editor"
                  value={output}
                  onChange={(e) => handleOutputChange(e.target.value)}
                  dir={lang === "ar" ? "rtl" : "ltr"}
                  className="min-h-[32rem] h-full rounded-none border-0 focus-visible:ring-0 resize-none p-8 text-sm leading-relaxed font-serif-display"
                />
              ) : (
                <div className="p-10">
                  <Markdown text={output || ""} streaming={streaming} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
